import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Minus, Sunrise, Utensils } from "lucide-react";
import Section, { PageHero } from "@/components/Section";
import Reveal from "@/components/Reveal";
import PlanCard from "@/components/PlanCard";
import CTAButton from "@/components/CTAButton";
import { FormNotice } from "@/components/FormControls";
import { useBrand } from "@/context/BrandContext";
import { planTypes, planDurations, buildPlan, allPlans, formatPrice } from "@/lib/plans";

const Cell = ({ value }) => {
  if (value === true) return <Check className="mx-auto h-4 w-4 text-coralInk" aria-label="Included" />;
  if (value === false) return <Minus className="mx-auto h-4 w-4 text-cocoa/35" aria-label="Not included" />;
  return <span className="text-sm text-cocoa/70">{value}</span>;
};

const typeIcons = { breakfast: Sunrise, lunch: Utensils, "breakfast-lunch": Utensils };

export default function PlansPage() {
  const brand = useBrand();
  const [params, setParams] = useSearchParams();
  const types = planTypes();
  const durations = planDurations();

  const initialType = types.some((t) => t.id === params.get("type"))
    ? params.get("type")
    : brand.mealPlans.defaultType;
  const initialDuration = durations.some((d) => d.id === params.get("duration"))
    ? params.get("duration")
    : brand.mealPlans.defaultDuration;

  const [typeId, setTypeId] = useState(initialType);
  const [durationId, setDurationId] = useState(initialDuration);

  const selectType = (id) => {
    setTypeId(id);
    setParams({ type: id, duration: durationId }, { replace: true });
  };
  const selectDuration = (id) => {
    setDurationId(id);
    setParams({ type: typeId, duration: id }, { replace: true });
  };

  const plan = buildPlan(typeId, durationId);
  const everyPlan = allPlans();
  const trialVisible = brand.trial.visible;

  const comparisonRows = [
    {
      label: "Meals per week",
      values: types.map((t) => String(t.mealsPerWeek)),
    },
    {
      label: "Dishes in rotation",
      values: types.map((t) => String(t.rotationCount)),
    },
    { label: "Breakfast included", values: types.map((t) => t.collection !== "lunch") },
    { label: "Lunch included", values: types.map((t) => t.collection !== "breakfast") },
    {
      label: "Age-based portions (2–4 / 5–7 / 8–10 / 11+)",
      values: types.map(() => true),
    },
    { label: "Dietary and allergy profile", values: types.map(() => true) },
    { label: "Parent note and child note", values: types.map(() => true) },
    {
      label: "Home delivery",
      values: types.map((t) => t.deliveryModes.includes("home")),
    },
    {
      label: "School delivery where available",
      values: types.map((t) => t.deliveryModes.includes("school")),
    },
    {
      label: "Priority WhatsApp support",
      values: types.map((t) => t.collection === "both"),
    },
  ];

  return (
    <>
      <PageHero
        eyebrow="Meal plans"
        title="Choose the meals, then choose the length."
        intro="Two decisions and you are done. Pick breakfast, lunch or both, then a single week or the complete three-week cycle. Every plan includes age-based portions, your child’s dietary profile and a note for you and for them with every meal."
      />

      <Section id="plan-builder">
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1fr] lg:items-start">
          <Reveal>
            <div className="mbm-card p-6 sm:p-7">
              <fieldset>
                <legend className="text-[12px] font-extrabold uppercase tracking-[0.18em] text-cocoa/70">
                  1 — Which meals?
                </legend>
                <div className="mt-4 flex flex-col gap-2.5">
                  {types.map((t) => {
                    const TypeIcon = typeIcons[t.id] || Utensils;
                    const active = t.id === typeId;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => selectType(t.id)}
                        aria-pressed={active}
                        data-testid={`plan-type-${t.id}`}
                        className={`flex items-center gap-3.5 rounded-control px-5 py-4 text-left transition-all duration-300 ${
                          active
                            ? "bg-amber text-cocoa shadow-soft"
                            : "bg-cream text-cocoa hover:bg-sageSoft"
                        }`}
                      >
                        <TypeIcon
                          className={`h-5 w-5 shrink-0 ${active ? "text-cocoa" : "text-coralInk"}`}
                          aria-hidden="true"
                        />
                        <span>
                          <span className="block text-sm font-extrabold">{t.label}</span>
                          <span
                            className={`mt-0.5 block text-[12px] ${
                              active ? "text-cocoa/75 font-semibold" : "text-cocoa/75"
                            }`}
                          >
                            {t.mealsPerWeek} meals a week · {t.rotationCount} dishes in rotation
                          </span>
                        </span>
                        {t.badge && (
                          <span
                            className={`ml-auto shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] ${
                              active ? "bg-coralInk text-white" : "bg-amber text-cocoa"
                            }`}
                          >
                            {t.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <fieldset className="mt-7">
                <legend className="text-[12px] font-extrabold uppercase tracking-[0.18em] text-cocoa/70">
                  2 — For how long?
                </legend>
                <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
                  {durations.map((d) => {
                    const active = d.id === durationId;
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => selectDuration(d.id)}
                        aria-pressed={active}
                        data-testid={`plan-duration-${d.id}`}
                        className={`rounded-control px-5 py-4 text-left transition-all duration-300 ${
                          active
                            ? "bg-amber text-cocoa shadow-soft"
                            : "bg-cream text-cocoa hover:bg-sageSoft"
                        }`}
                      >
                        <span className="block text-sm font-extrabold">{d.label}</span>
                        <span
                          className={`mt-0.5 block text-[12px] ${
                            active ? "text-cocoa/75 font-semibold" : "text-cocoa/75"
                          }`}
                        >
                          {d.shortLabel}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-[13px] leading-relaxed text-cocoa/70">
                  {durations.find((d) => d.id === durationId)?.blurb}
                </p>
              </fieldset>

              <div className="mt-7">
                <FormNotice tone="warn" testId="pricing-notice">
                  Prices are not published yet. {brand.payments.note}
                </FormNotice>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <p className="mbm-eyebrow">Your plan</p>
            <div className="mt-4" data-testid="selected-plan">
              <PlanCard plan={plan} />
            </div>
          </Reveal>
        </div>
      </Section>

      <Section
        id="all-plans"
        tone="creamDeep"
        eyebrow="Every combination"
        title="All six plans at a glance"
        intro="The same six plans you can build above, listed together."
      >
        <div className="mt-12 overflow-x-auto rounded-card bg-surface shadow-soft">
          <table className="w-full min-w-[620px] border-collapse text-left">
            <thead>
              <tr className="bg-forest text-white">
                <th scope="col" className="px-5 py-4 text-xs font-extrabold uppercase tracking-[0.12em]">
                  Plan
                </th>
                <th scope="col" className="px-5 py-4 text-xs font-extrabold uppercase tracking-[0.12em]">
                  Meals
                </th>
                <th scope="col" className="px-5 py-4 text-xs font-extrabold uppercase tracking-[0.12em]">
                  Price
                </th>
                <th scope="col" className="px-5 py-4" />
              </tr>
            </thead>
            <tbody>
              {everyPlan.map((p, i) => (
                <tr key={p.id} className={i % 2 ? "bg-creamDeep/50" : ""} data-testid={`plan-row-${p.id}`}>
                  <th scope="row" className="px-5 py-4">
                    <span className="block text-sm font-extrabold text-coralInk">
                      {p.typeLabel}
                    </span>
                    <span className="mt-0.5 block text-[12px] font-medium text-cocoa/65">
                      {p.durationLabel} · {p.weeks === 1 ? "1 week" : `${p.weeks} weeks`}
                    </span>
                  </th>
                  <td className="px-5 py-4 text-sm text-cocoa/70">
                    {p.totalMeals} meals
                    <span className="block text-[12px] text-cocoa/75">
                      {p.mealsPerWeek} per week
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm font-medium text-cocoa">{formatPrice(p)}</td>
                  <td className="px-5 py-4 text-right">
                    <CTAButton
                      to={`/checkout?plan=${p.id}`}
                      variant="outline"
                      className="!min-h-[44px] whitespace-nowrap !px-4 !text-[13px]"
                      data-testid={`plan-row-cta-${p.id}`}
                    >
                      Choose
                    </CTAButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        id="plan-comparison"
        eyebrow="Compare"
        title="What is included in each plan type"
      >
        <div className="mt-10 overflow-x-auto rounded-card bg-surface shadow-soft">
          <table className="w-full min-w-[600px] border-collapse text-left">
            <thead>
              <tr className="bg-forest text-white">
                <th scope="col" className="px-5 py-4 text-xs font-extrabold uppercase tracking-[0.12em]">
                  Feature
                </th>
                {types.map((t) => (
                  <th
                    key={t.id}
                    scope="col"
                    className="px-5 py-4 text-center text-xs font-extrabold uppercase tracking-[0.12em]"
                  >
                    {t.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, i) => (
                <tr key={row.label} className={i % 2 ? "bg-creamDeep/50" : ""}>
                  <th scope="row" className="px-5 py-4 text-sm font-medium text-cocoa">
                    {row.label}
                  </th>
                  {row.values.map((v, vi) => (
                    <td key={`${row.label}-${vi}`} className="px-5 py-4 text-center">
                      <Cell value={v} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!trialVisible && (
          <p className="mt-5 text-sm text-cocoa/70" data-testid="trial-hidden-note">
            A single trial meal may be offered in selected areas later. It is not available yet.
          </p>
        )}
      </Section>
    </>
  );
}
